//
// # Changes & Change Log
//
// The undo-redo queue and its member types.
//

import { Result, Ok, Err } from "CoreUtils";
import { EntityKind } from "./entity";

// Enumerate Kinds of Changes
// Each `Change` variant has a `kind` field that is one of these values.
export const enum Kind {
  Add = "Add",
  Remove = "Remove",
  Update = "Update",
  Combo = "Combo",
}

// Structural constraint for `Add` / `Remove` change targets.
// Requires an `entityKind` field, which every real Entity carries.
// This catches callers that mistakenly pass `Update`-style argument
// objects (`{ target, from, to }`) to `add` / `remove`: those literals
// have no `entityKind` and so fail the constraint at compile time.
export interface ChangeEntity {
  readonly entityKind: EntityKind;
}

export class Add<T extends ChangeEntity> {
  readonly kind = Kind.Add; // Kind Tag
  private constructor(public entity: T) {}
  static new<T extends ChangeEntity>(entity: T): Add<T> {
    return new Add(entity);
  }
  inverse = (): Remove<T> => {
    return Remove.new(this.entity);
  };
}

export function add<T extends ChangeEntity>(entity: T): Add<T> {
  return Add.new(entity);
}

export class Remove<T extends ChangeEntity> {
  readonly kind = Kind.Remove; // Kind Tag
  private constructor(public entity: T) {}
  static new<T extends ChangeEntity>(entity: T): Remove<T> {
    return new Remove(entity);
  }
  inverse = (): Add<T> => {
    return Add.new(this.entity);
  };
}

export function remove<T extends ChangeEntity>(entity: T): Remove<T> {
  return Remove.new(entity);
}

// # Update
//
// Widely used to update an existing entity's data, e.g. changing parameters or locations.
// Now stores the original target, from, and to data for schema conversion.
//
export class Update {
  readonly kind = Kind.Update; // Kind Tag

  private constructor(
    public apply: () => unknown,
    public undo: () => unknown,
    // Store original update arguments for schema conversion
    public readonly target?: unknown,
    public readonly from?: unknown,
    public readonly to?: unknown
  ) {}
  static new<Data>(args: UpdateArgs<Data>): Update {
    const { target, from, to } = args;
    const undoFn = () => target.update(from);
    const applyFn = () => target.update(to);
    return new Update(applyFn, undoFn, target, from, to);
  }
  inverse = (): Update => {
    const { apply, undo, target, from, to } = this;
    return new Update(undo, apply, target, to, from);
  };
}

export interface UpdateArgs<Data> {
  target: Updatable<Data>;
  from: Data;
  to: Data;
}

// Trait for anything with an `update` method that accepts a `DataT` argument.
export interface Updatable<DataT> {
  update(data: DataT): unknown;
}

export function update<Data>(args: UpdateArgs<Data>): Update {
  return Update.new(args);
}

// Single/ Atomic Change
export type Single<T extends ChangeEntity> = Add<T> | Remove<T> | Update;

// # Combo Change
//
// A `Combo` is a list of `Single` changes that should be treated as a single change.
// Structured as a `first` `Single` change, and a list of `rest` `Single` changes,
// to ensure at the type-level that it can never be empty.
//
export class Combo<T extends ChangeEntity> {
  readonly kind = Kind.Combo; // Kind Tag
  private constructor(readonly changes: Array<Single<T>>) {}
  // Factory method for creating a `Combo` from a `first` `Single` change.
  // The call signature here is what ensures that the `Combo` can never be empty.
  static new<T extends ChangeEntity>(
    first: Single<T>,
    ...changes: Array<Single<T>>
  ): Combo<T> {
    return new Combo([first, ...changes]);
  }
  // Alternately build from an array, but get back a result indicating it wasn't empty.
  static fromArray<T extends ChangeEntity>(
    arr: Array<Single<T>>
  ): Result<Combo<T>> {
    if (!arr.length) return Err({ why: "empty-combo" });
    return Ok(new Combo(arr));
  }
  // Inverse of a sequence is the inverse operations in reverse order —
  // like unwinding a stack, so removals happen before dependent additions.
  inverse = (): Combo<T> => {
    return new Combo(this.changes.map((change) => change.inverse()).reverse());
  };
}

// The primary `Change` union type
export type Change<T extends ChangeEntity> = Single<T> | Combo<T>;

// # Change Log
//
// A list of changes for undo/redo.
// Note key methods `undo` and `redo` *return* `Change`s to be made.
// The `ChangeLog` itself is not responsible for making the changes.
//
export class ChangeLog<T extends ChangeEntity> {
  // List of Changes
  changes: Array<Change<T>> = [];
  // Current head pointer. Potentially in the middle of the list during undo/ redo actions.
  head: number = -1;

  // Boolean indication of whether there are any currently undone changes.
  get undoing(): boolean {
    return this.head < this.changes.length - 1;
  }

  // Add a new change. Drops all changes after the current head.
  add(change: Change<T>): void {
    if (this.undoing) {
      // Chop off any undone changes
      this.changes = this.changes.slice(0, this.head + 1);
    }
    this.changes.push(change);
    this.head += 1;
  }

  // Replace the most recent `count` logged changes with a single `Combo` that
  // contains all of their underlying `Single` changes (existing `Combo`s are
  // flattened in order). After this the merged changes undo/redo as one atomic
  // step. Used when a multi-step operation that lands several separate entries
  // on the log (e.g. recreate + re-orient during a type-change rollback) must
  // appear to the user as a single undo entry.
  //
  // Total (never fails): if there are undone (redo-able) changes ahead of the
  // head they are discarded first — as `add` does — so a fold never captures an
  // entry a redo could resurrect. `count` is clamped to the changes actually
  // present, and fewer than 2 combinable entries (0 or 1) is already atomic, so
  // it is a no-op.
  combineLast(count: number): void {
    // Combining is a forward-history operation, so first drop any undone
    // changes after the head (mirrors `add`) — we only ever fold applied
    // history.
    if (this.undoing) {
      this.changes = this.changes.slice(0, this.head + 1);
    }
    const n = Math.min(count, this.changes.length);
    if (n < 2) return; // 0 or 1 entries are already atomic — nothing to combine
    const tail = this.changes.slice(this.changes.length - n);
    const flat: Array<Single<T>> = [];
    for (const change of tail) {
      if (change.kind === Kind.Combo) flat.push(...change.changes);
      else flat.push(change);
    }
    // `flat` holds at least `n >= 2` entries, so the Combo is never empty; the
    // `err` branch is unreachable but keeps the function assertion-free.
    const combo = Combo.fromArray(flat);
    if (combo.err) return;
    this.changes = this.changes.slice(0, this.changes.length - n);
    this.changes.push(combo.val);
    this.head = this.changes.length - 1;
  }

  // Drop the most recent `count` changes from the log without applying anything.
  // Used to discard speculative record-only entries when a multi-step operation
  // aborts partway (e.g. a type-change rollback). Assumes the head is at the end
  // of the log (no pending redo); no-op for count <= 0; clamps to the number of
  // entries present.
  dropLast(count: number): void {
    if (count <= 0) return;
    const n = Math.min(count, this.changes.length);
    this.changes = this.changes.slice(0, this.changes.length - n);
    this.head = this.changes.length - 1;
  }

  // Get the Change that undoes the current head-pointer Change.
  // Decrements the head pointer along the way.
  undo(): Change<T> | null {
    if (this.head < 0) {
      return null;
    }
    const change = this.changes[this.head];
    this.head -= 1;
    return change.inverse();
  }

  // Get the `Change` that redoes the most recently undone `Change`.
  // I.e. when we have "undone" changes, and the head pointer is not at the end,
  // get the head pointer `Change`. Increments the head pointer along the way.
  redo(): Change<T> | null {
    if (this.head >= this.changes.length - 1) {
      return null;
    }
    this.head += 1;
    return this.changes[this.head];
  }
}
